import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { UserPlus, ExternalLink } from "lucide-react";
import { format } from "date-fns";

export function OnboardingQueueView() {
  const { currentWorkspace } = useWorkspace();

  const { data: requests, isLoading } = useQuery({
    queryKey: ['onboarding-requests', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      
      const { data, error } = await supabase
        .from('onboarding_requests')
        .select(`
          *,
          bills (
            id,
            bill_number,
            amount
          )
        `)
        .eq('workspace_id', currentWorkspace.id)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentWorkspace?.id
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Onboarding Queue</CardTitle>
        <CardDescription>
          Bills waiting for new account creation
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : !requests || requests.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No pending onboarding requests
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Account Number</TableHead>
                <TableHead>Customer Name</TableHead>
                <TableHead>Bill Reference</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {requests.map((request: any) => (
                <TableRow key={request.id}>
                  <TableCell className="font-mono">{request.raw_account_number}</TableCell>
                  <TableCell>{request.raw_customer_name || '—'}</TableCell>
                  <TableCell>
                    {request.bills ? (
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{request.bills.bill_number}</span>
                        <span className="text-sm text-muted-foreground">
                          ${request.bills.amount?.toFixed(2)}
                        </span>
                      </div>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {format(new Date(request.created_at), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {request.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="default">
                        <UserPlus className="h-4 w-4 mr-2" />
                        Create Account
                      </Button>
                      {request.bill_id && (
                        <Button size="sm" variant="outline">
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
